import { FormPageContainer } from '@strata/react-form-header-and-content';
import { Worklist, type WorklistTask, WorklistWithFilters } from '@strata/react-worklist';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useState } from 'react';

import { BlockTasksDialog } from 'components/worklist/dialogs/BlockTasksDialog';
import { ConfirmLddAccessDialog } from 'components/worklist/dialogs/ConfirmLddAccessDialog';
import { DenyLddAccessDialog } from 'components/worklist/dialogs/DenyLddAccessDialog';
import { usePermissionCheck, useWorklistTasks } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import {
    useOnDoNotConvert,
    useOnTaskActionClick,
    useOnUnableToComplete,
} from 'services/worklistHelpers';
import { useAppDispatch } from 'store';
import { usePostClearWorklistTasksMutation } from 'store/api/worklistApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

export const WorklistPage = () => {
    const dispatch = useAppDispatch();
    const loggedInSupplierName = useLoggedInSupplierName();
    const worklistTasks = useWorklistTasks();
    const { useTimeLimitedCompletedWorklistTasks } = useFlags<{
        useTimeLimitedCompletedWorklistTasks: boolean;
    }>();

    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);

    const onReferralTaskActionClick = useOnTaskActionClick(true);
    const onUnableToComplete = useOnUnableToComplete();
    const onDoNotConvert = useOnDoNotConvert();
    const [lddTask, setLddTask] = useState<WorklistTask>();
    const [lddAction, setLddAction] =
        useState<(typeof Worklist.WorklistTaskAction)[keyof typeof Worklist.WorklistTaskAction]>();
    const [taskToBlock, setTaskToBlock] = useState<WorklistTask>();

    const handleCloseLddDialog = () => {
        setLddTask(undefined);
        setLddAction(undefined);
    };

    const handleTaskActionClick: Required<
        ComponentProps<typeof WorklistWithFilters>
    >['onTaskActionClick'] = (worklistTask, taskAction) => {
        if (worklistTask.task_type === Worklist.WorkTaskType.LDDUPDATE) {
            setLddTask(worklistTask);
            setLddAction(taskAction);
            return;
        }
        if (
            worklistTask.task_type === Worklist.WorkTaskType.CONVERSION &&
            taskAction === Worklist.WorklistTaskAction.DO_NOT_CONVERT
        ) {
            onDoNotConvert(worklistTask);
            return;
        }
        if (taskAction === Worklist.WorklistTaskAction.HIDE_TASKS) {
            setTaskToBlock(worklistTask);
            return;
        }
        onReferralTaskActionClick(worklistTask, taskAction);
    };

    const [postClearWorklistTasks] = usePostClearWorklistTasksMutation();
    const onClearTasksClick = (taskIds: string[]) => {
        void (async () => {
            await postClearWorklistTasks({ taskIds }).unwrap();
            await dispatch(fetchWorklistTasks());
        })();
    };

    const {
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.WORKLIST);

    return (
        <FormPageContainer>
            <WorklistWithFilters
                title="Worklist"
                secondaryTitle={loggedInSupplierName}
                tasks={worklistTasks}
                onClearTasksClick={writeWorklist ? onClearTasksClick : undefined}
                onUnableToComplete={writeWorklist ? onUnableToComplete : undefined}
                onTaskActionClick={handleTaskActionClick}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                searchText={search}
                onSearch={updateGridSearch}
                limited={useTimeLimitedCompletedWorklistTasks}
            />
            <ConfirmLddAccessDialog
                isOpen={lddAction === Worklist.WorklistTaskAction.CONFIRM_ACCESS}
                worklistTask={lddTask}
                onClose={handleCloseLddDialog}
            />
            <DenyLddAccessDialog
                isOpen={lddAction === Worklist.WorklistTaskAction.DENY_ACCESS}
                worklistTask={lddTask}
                onClose={handleCloseLddDialog}
            />
            <BlockTasksDialog
                isOpen={!!taskToBlock}
                worklistTask={taskToBlock}
                onClose={() => setTaskToBlock(undefined)}
            />
        </FormPageContainer>
    );
};
